import { createPortal } from 'react-dom';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '@/components/ui/Icon';
import { useAuth } from '@/hooks/useAuth';
import { useI18n } from '@/hooks/useI18n';
import { routes } from '@/lib/routes';
import '@/styles/za180-consultant.css';

const HOST_ID = 'za180-launcher-host';

/** Idempotent body host — prevents duplicate portals on remount / Strict Mode. */
const ensureLauncherHost = (): HTMLElement => {
  let host = document.getElementById(HOST_ID);
  if (!host) {
    host = document.createElement('div');
    host.id = HOST_ID;
    document.body.appendChild(host);
  }
  return host;
};

/**
 * Single app-shell AI consultant launcher.
 * Portaled to a dedicated body host + position:fixed so it stays viewport bottom-right,
 * survives route switches without remounting the DOM node out of existence,
 * and clears only when leaving the authenticated AppLayout (via host empty render).
 */
export const ChatLauncher = () => {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [host, setHost] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setHost(ensureLauncherHost());
  }, []);

  if (!user || !host) return null;

  const hidden = pathname === routes.assistant;

  return createPortal(
    <button
      type="button"
      className={`za180-launcher${hidden ? ' is-route-hidden' : ''}`}
      data-app-shell-launcher="consultant-ai"
      aria-label={t('consultant.launcher')}
      aria-hidden={hidden}
      hidden={hidden}
      tabIndex={hidden ? -1 : 0}
      onClick={() => navigate(routes.assistant)}
    >
      <Icon name="chat" size={22} strokeWidth={1.8} />
      <span className="label">{t('consultant.launcher')}</span>
      <span className="za180-badge" aria-hidden="true" />
    </button>,
    host,
  );
};
